import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Analytics } from "@vercel/analytics/react";

// Context
import { CookieConsentProvider } from "./context/CookieConsentContext";

// Utils & Constants
import { EVENTS, HALLOWEEN_FAQS, DEFAULT_OG_IMAGE } from "./utils/constants";
import { getEventBySlug } from "./utils/helpers";
import { trackPageView, trackViewContent } from "./utils/tracking";

// Common Components
import { Navigation } from "./components/common/Navigation";
import { FloatingCTA } from "./components/common/FloatingCTA";
import { Seo } from "./components/common/Seo";

// Pages
import { HomePage } from "./components/pages/HomePage";
import { PressPage } from "./components/pages/PressPage";
import { AboutUsPage } from "./components/pages/AboutUsPage";
import { EventsPage } from "./components/pages/EventsPage";
import { SingleEventPage } from "./components/pages/SingleEventPage";
import { LatinEventAmsterdamPage, LatinEventRotterdamPage } from "./components/pages/SEOPages";
import { KingsdayWeekenderPage } from "./components/pages/KingsdayWeekenderPage";
import { PadelXReggaetonPage } from "./components/pages/PadelXReggaetonPage";
import { LiveShowExperiencePage, CasitaExperiencePage } from "./components/pages/ExperiencePages";
import { HalloweenPage } from "./components/pages/HalloweenPage";
import { TermsPage } from "./components/pages/TermsPage";
import { PrivacyPage } from "./components/pages/PrivacyPage";
import { QuizPage } from "./quiz/QuizPage";
import { QuizAdminPage } from "./quiz/QuizAdminPage";
import { UnsubscribePage } from "./quiz/UnsubscribePage";
import { Halloween2027Page, H27UnsubscribePage } from "./halloween2027/Halloween2027Page";

// ---------------------------------------------------------------------------
// Path-based routing (real URLs, crawlable + prerenderable).
// Legacy hash links (#/halloween, #events) redirect to their path equivalents.
// ---------------------------------------------------------------------------

const normalizePath = (p) => {
  let path = p || "/";
  if (!path.startsWith("/")) path = "/" + path;
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path;
};

// Translate a legacy hash ("#/halloween", "#events", "#/?scrollTo=x") to a path+search
const legacyHashToUrl = (hash) => {
  const raw = hash.replace(/^#/, "");
  if (!raw || raw === "/") return null;
  // In-page anchors used by section scrolling (e.g. "#agenda") map to ids on
  // the current page — only treat known legacy ROUTE hashes as redirects.
  const routeHashes = ["events", "about", "press"];
  const [pathPart, query] = raw.split("?");
  if (raw.startsWith("/")) {
    return normalizePath(pathPart) + (query ? `?${query}` : "");
  }
  if (routeHashes.includes(pathPart)) return `/${pathPart}`;
  return null;
};

// Page-specific structured data for /halloween: a Halloween-only FAQPage (built
// from the same HALLOWEEN_FAQS the page renders) plus breadcrumbs. Module-level
// so the reference is stable across renders.
const HALLOWEEN_JSON_LD = [
  // The teaser shown on /halloween, so it can surface in Google video results
  {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: "The Amsterdam Latin Halloween Festival 2026 teaser | Baila Dembow",
    description:
      "The nightmare wakes up all over again. The Amsterdam Latin Halloween Festival by Baila Dembow, Saturday 31 October 2026 at IJLAND Amsterdam.",
    thumbnailUrl: ["https://bailadembow.com/images/halloween/schema/latin-halloween-teaser-thumb.jpg"],
    uploadDate: "2026-10-05T12:00:00+02:00",
    duration: "PT22S",
    contentUrl: "https://bailadembow.com/videos/halloween-teaser.mp4",
    publisher: {
      "@type": "Organization",
      name: "Baila Dembow",
      logo: { "@type": "ImageObject", url: "https://bailadembow.com/images/branding/baila-dembow-logo-512.png" },
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: HALLOWEEN_FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Baila Dembow", item: "https://bailadembow.com/" },
      { "@type": "ListItem", position: 2, name: "Events", item: "https://bailadembow.com/events" },
      { "@type": "ListItem", position: 3, name: "Amsterdam Latin Halloween Festival", item: "https://bailadembow.com/halloween" },
    ],
  },
];

// Per-route head tags — unique title (<60 chars), description and canonical.
// Link-preview card for the homepage and /events: the soonest upcoming
// featured event's own card (og_image), else the evergreen brand card. This is
// baked into the prerendered HTML at build time, so once a flagship event is
// past it falls back by itself on the next deploy — it can't strand last
// season's poster as the site-wide share image again.
const OG_CARD_SIZE = { imageWidth: 1200, imageHeight: 630 };
const sharedCard = () => {
  const today = new Date().toISOString().slice(0, 10);
  const featured = EVENTS
    .filter((e) => e.featured && e.date >= today && e.og_image)
    .sort((a, b) => (a.featured_priority || 99) - (b.featured_priority || 99) || a.date.localeCompare(b.date))[0];   // same order as the homepage spotlight
  return { image: featured?.og_image || DEFAULT_OG_IMAGE, ...OG_CARD_SIZE };
};
const halloweenCard = () => ({
  image: EVENTS.find((e) => e.landing_page === "/halloween")?.og_image || DEFAULT_OG_IMAGE,
  ...OG_CARD_SIZE,
});

const routeSeo = (path, event) => {
  const seo = {
    "/": {
      title: "Latin Event Amsterdam | Reggaeton & Dembow – Baila Dembow",
      description:
        "Baila Dembow is the leading Latin event in Amsterdam and the Netherlands. Reggaeton, dembow, salsa and bachata parties — see upcoming dates and get tickets.",
      ...sharedCard(),
    },
    "/events": {
      title: "Upcoming Latin Events & Parties | Baila Dembow",
      description:
        "All upcoming Baila Dembow events in the Netherlands: reggaeton and dembow nights in Amsterdam, Groningen and beyond. Dates, venues and tickets.",
      ...sharedCard(),
    },
    "/halloween": {
      title: "SOLD OUT: Amsterdam Latin Halloween Festival 2026 | Baila Dembow",
      description:
        "Amsterdam's biggest Latin Halloween party. Sat 31 October 2026 at IJLAND: SOLD OUT. Only buy resale from a reputable reseller. Sign up for the Halloween 2027 early release.",
      ...halloweenCard(),
      jsonLd: HALLOWEEN_JSON_LD,
    },
    "/experiences/halloween": {
      path: "/halloween", // same page as /halloween: canonical to the main URL
      title: "SOLD OUT: Amsterdam Latin Halloween Festival 2026 | Baila Dembow",
      description:
        "Amsterdam's biggest Latin Halloween party. Sat 31 October 2026 at IJLAND: SOLD OUT. Only buy resale from a reputable reseller. Sign up for the Halloween 2027 early release.",
      ...halloweenCard(),
      jsonLd: HALLOWEEN_JSON_LD,
    },
    "/about": {
      title: "About Baila Dembow | Latin Events Netherlands",
      description:
        "The story of Baila Dembow — the team bringing reggaeton, dembow and Latin culture to dancefloors across the Netherlands since 2023.",
    },
    "/press": {
      title: "Press & Media | Baila Dembow",
      description: "Press information, media assets and contact for Baila Dembow, the leading Latin event brand in the Netherlands.",
    },
    "/privacy": {
      title: "Privacy Notice | Baila Dembow",
      description: "How Baila Dembow handles your data: the Halloween quiz, email, cookies and your rights.",
    },
    "/terms": {
      title: "Terms & Conditions | Baila Dembow",
      description: "Door policy, ticket terms, house rules and photo/video consent for Baila Dembow events.",
    },
    "/latin-event-amsterdam": {
      title: "Latin Event in Amsterdam | Baila Dembow",
      description:
        "Looking for a Latin event in Amsterdam? Baila Dembow hosts the city's biggest reggaeton and dembow parties. See upcoming Amsterdam dates and tickets.",
    },
    "/latin-event-rotterdam": {
      title: "Latin Event in Rotterdam | Baila Dembow",
      description:
        "Looking for a Latin event in Rotterdam? Baila Dembow brings reggaeton, dembow and Latin heat to Rotterdam. See upcoming dates and tickets.",
    },
    "/experiences/live-tribute": {
      title: "Live Tribute Shows | Baila Dembow",
      description:
        "Baila Dembow Live Tribute Shows — world-class musicians honouring Latin legends live on stage, followed by reggaeton and dembow until sunrise.",
    },
    "/experiences/casita": {
      title: "Casa de Baila Dembow — La Casita | Baila Dembow",
      description:
        "La Casita: the full-scale house that turns any venue into a Latin American street party, with archways, straw hats and the iconic Baila Dembow neon.",
    },
  };
  if (seo[path]) return { path, ...seo[path] };
  if (event) {
    return {
      title: `${event.title} | Baila Dembow`.slice(0, 70),
      description: `${event.city} · ${event.venue} · ${event.date} · ${event.time}. ${event.tagline || "Reggaeton, dembow and Latin hits."} Tickets on sale now.`,
      // Events with a dedicated landing page (Halloween) point their canonical
      // there, so the two URLs don't compete for the same searches.
      path: event.landing_page || path,
      image: event.og_image || event.image_url,
      ...(event.og_image ? OG_CARD_SIZE : {}),
    };
  }
  return { ...seo["/"], path: "/" };
};

// Main App Component
function App() {
  const events = EVENTS;

  const getPath = () => normalizePath(window.location.pathname);
  const [currentPath, setCurrentPath] = useState(getPath);

  // Legacy hash redirect — run before first paint effects
  useEffect(() => {
    const target = legacyHashToUrl(window.location.hash);
    if (target) {
      window.history.replaceState(null, "", target);
      setCurrentPath(normalizePath(target.split("?")[0]));
    }
  }, []);

  // scrollTo support: /?scrollTo=agenda scrolls to the section after load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const scrollToId = params.get("scrollTo");
    if (scrollToId) {
      setTimeout(() => {
        document.getElementById(scrollToId)?.scrollIntoView({ behavior: "smooth" });
        window.history.replaceState(null, "", window.location.pathname);
      }, 400);
    }
  }, [currentPath]);

  // SPA navigation: intercept same-origin link clicks + handle back/forward
  useEffect(() => {
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (a.target === "_blank" || a.hasAttribute("download")) return;
      if (href.startsWith("#") || href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      if (!href.startsWith("/")) return;
      // Static files (e.g. /play.html) should load normally
      if (/\.[a-z0-9]+$/i.test(href.split("?")[0])) return;
      e.preventDefault();
      const [pathOnly, query] = href.split("?");
      window.history.pushState(null, "", href);
      setCurrentPath(normalizePath(pathOnly));
      if (!query || !query.includes("scrollTo=")) window.scrollTo(0, 0);
    };
    const onPop = () => setCurrentPath(getPath());
    document.addEventListener("click", onClick);
    window.addEventListener("popstate", onPop);
    return () => {
      document.removeEventListener("click", onClick);
      window.removeEventListener("popstate", onPop);
    };
  }, []);

  const effectivePath = currentPath;

  const isEventPage = effectivePath.startsWith("/events/");
  const eventSlug = isEventPage ? effectivePath.replace(/^\/events\//, "") : null;
  const isKingsdayPage = eventSlug === "kingsday-weekender-2026";
  // Padel × Reggaeton — accept both CamelCase and kebab-case slug forms
  const isPadelPage = eventSlug === "PadelXReggaeton" || eventSlug === "padel-x-reggaeton";
  // Standalone branded landing pages hide the global Nav + FloatingCTA chrome
  // The unlisted Halloween quiz campaign (/halloween/quiz and its verify/admin/unsubscribe pages) is
  // standalone too: no global nav, no floating ticket CTA, and always noindex.
  const isQuizRoute = effectivePath === "/halloween/quiz" || effectivePath.startsWith("/halloween/quiz/");
  // Halloween 2027 early-release list: standalone like the quiz; hidden by the API until 1 Nov 2026 05:00.
  const isH27Route = effectivePath === "/halloween/2027" || effectivePath.startsWith("/halloween/2027/");
  const isStandalonePage = isKingsdayPage || isPadelPage || isQuizRoute || isH27Route;

  const currentEvent = isEventPage && !isStandalonePage && eventSlug ? getEventBySlug(eventSlug, events) : null;
  const seoMeta = routeSeo(effectivePath, currentEvent);

  // Point the floating "GET TICKETS" CTA at this event's real checkout link
  // when we're on a page dedicated to one event, instead of the generic Linktree.
  const floatingCtaEvent =
    currentEvent ||
    (effectivePath === "/halloween" || effectivePath === "/experiences/halloween"
      ? events.find((e) => e.landing_page === "/halloween")
      : null);

  // Record every page a visitor actually hits (not just the first one on a
  // hard load) so the Meta/TikTok pixels can build a retargeting audience out
  // of it — e.g. "people who viewed the Halloween page". loadMetaPixel /
  // loadTikTokPixel already send the first PageView for whichever page a
  // visitor lands on directly, so only re-fire that on later route changes;
  // ViewContent (ideal for retargeting a specific event page) is new, so it
  // fires on the first render too.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (!isFirstRender.current) trackPageView();
    isFirstRender.current = false;
    if (floatingCtaEvent) trackViewContent(floatingCtaEvent);
  }, [effectivePath]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CookieConsentProvider>
      {/* Standalone legacy landing pages manage their own head tags */}
      {!isStandalonePage && <Seo {...seoMeta} />}
      {isQuizRoute && (
        <Seo
          noindex
          title="Halloween Quiz | Baila Dembow"
          description="Five questions. One Halloween reward."
          path="/halloween/quiz"
        />
      )}
      {isH27Route && (
        <Seo
          noindex
          title="Baila Dembow — Halloween 2027"
          description="Sign up for Halloween 2027 updates from Baila Dembow: The Latin Halloween Indoor Fest, October 2027."
          path="/halloween/2027"
        />
      )}
      <div className="grain-overlay" />
      {!isStandalonePage && <Navigation currentPath={effectivePath} />}
      <main>
        {effectivePath === "/halloween/2027/unsubscribe" ? (
          <H27UnsubscribePage />
        ) : effectivePath === "/halloween/2027" ? (
          <Halloween2027Page />
        ) : effectivePath === "/halloween/quiz/admin" ? (
          <QuizAdminPage />
        ) : effectivePath === "/halloween/quiz/unsubscribe" ? (
          <UnsubscribePage />
        ) : effectivePath === "/halloween/quiz/verify" ? (
          <QuizPage mode="verify" />
        ) : effectivePath === "/halloween/quiz" ? (
          <QuizPage />
        ) : effectivePath === "/privacy" ? (
          <PrivacyPage />
        ) : effectivePath === "/press" ? (
          <PressPage />
        ) : effectivePath === "/terms" ? (
          <TermsPage />
        ) : effectivePath === "/latin-event-amsterdam" ? (
          <LatinEventAmsterdamPage events={events} />
        ) : effectivePath === "/latin-event-rotterdam" ? (
          <LatinEventRotterdamPage events={events} />
        ) : effectivePath === "/halloween" || effectivePath === "/experiences/halloween" ? (
          <HalloweenPage />
        ) : effectivePath === "/experiences/live-tribute" ? (
          <LiveShowExperiencePage />
        ) : effectivePath === "/experiences/casita" ? (
          <CasitaExperiencePage />
        ) : isKingsdayPage ? (
          <KingsdayWeekenderPage />
        ) : isPadelPage ? (
          <PadelXReggaetonPage />
        ) : isEventPage && eventSlug ? (
          <SingleEventPage eventSlug={eventSlug} events={events} />
        ) : effectivePath === "/events" ? (
          <EventsPage events={events} />
        ) : effectivePath === "/about" ? (
          <AboutUsPage />
        ) : (
          <HomePage events={events} />
        )}
      </main>
      {!isStandalonePage && (
        <FloatingCTA
          ticketUrl={floatingCtaEvent?.sold_out ? "/halloween/2027" : floatingCtaEvent?.ticket_url}
          eventName={floatingCtaEvent?.sold_out ? `${floatingCtaEvent.title} - sold out, 2027 signup` : floatingCtaEvent?.title}
          label={floatingCtaEvent?.sold_out ? "HALLOWEEN 2027" : "GET TICKETS"}
        />
      )}
      <Toaster position="top-center" richColors />
      <Analytics />
    </CookieConsentProvider>
  );
}

export default App;
